// Conversion + engagement tracking — fires Meta Pixel, Google Analytics 4,
// and Google Ads conversion events in lockstep.
// All 3 pixels are initialized DIRECTLY in /public/index.html <head>.

// Google Ads conversion labels (public values, same as Google Ads provides).
const GADS = {
  LEAD:     "AW-18227590413/mnAuCLvft74cEI3qy_ND", // AutoHQ Lead Captured
  BOOKING:  "AW-18227590413/CBD5CODfnb4cEI3qy_ND", // AutoHQ Service Booking
  WHATSAPP: "AW-18227590413/GycHCNaws74cEI3qy_ND", // AutoHQ WhatsApp Contact
};

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Read current A/B variant (set by lib/abTest.js). Tagged onto every event so
// we can split conversion rates by variant in Meta Ads Manager + GA4.
const _variant = () => {
  if (typeof window === "undefined") return "a";
  try { return localStorage.getItem("autohq_ab_cp_headline") || "a"; }
  catch { return "a"; }
};

const _eventId = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID().replace(/-/g, "");
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
};

const _readCookie = (name) => {
  if (typeof document === "undefined") return null;
  const m = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]+)"));
  return m ? decodeURIComponent(m[1]) : null;
};

/** Send the SAME event server-side via Meta CAPI for iOS/ad-blocker resilience.
 * Uses event_id to dedupe against the browser fbq call. Fire-and-forget. */
const _capi = (eventName, eventId, { phone, name, email, externalId } = {}, customData = {}) => {
  if (typeof fetch === "undefined") return;
  const body = {
    event_name: eventName,
    event_id: eventId,
    event_source_url: typeof window !== "undefined" ? window.location.href : "",
    phone, email, name, external_id: externalId,
    fbp: _readCookie("_fbp"),
    fbc: _readCookie("_fbc"),
    ...customData,
    variant: _variant(),  // A/B tag
  };
  // Strip undefined to keep payload clean
  Object.keys(body).forEach((k) => body[k] === undefined && delete body[k]);
  fetch(`${API_BASE}/track/meta`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    keepalive: true,  // ensure delivery even on page-unload
  }).catch(() => { /* swallow — analytics never fails the UX */ });
};

const _fbq = (name, ...args) => {
  if (typeof window !== "undefined" && typeof window.fbq === "function") {
    // Inject variant into the params object (second positional arg) for trackCustom
    // and track calls. fbq tolerates extra fields.
    if (args.length >= 1 && args[0] && typeof args[0] === "object") {
      args[0] = { ...args[0], variant: _variant() };
    }
    window.fbq(name, ...args);
  }
};

const _ga = (eventName, params = {}) => {
  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag("event", eventName, { ...params, experiment_variant: _variant() });
  }
};

const _gAdsConversion = (sendTo, value, currency = "INR", txnId) => {
  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    const params = { send_to: sendTo, value, currency };
    if (txnId) params.transaction_id = txnId;
    window.gtag("event", "conversion", params);
  }
};

/**
 * Meta Pixel Advanced Matching — feeds hashed customer data on subsequent events
 * for ~30% better match rate on iOS / ad-blocked traffic.
 * Pass un-hashed phone/email/name — Facebook hashes them client-side automatically
 * via the `fbq('init', PIXEL_ID, params)` form.
 */
export function setAdvancedMatching({ phone, name, email } = {}) {
  if (typeof window === "undefined" || typeof window.fbq !== "function") return;
  const data = {};
  if (phone) data.ph = ("91" + phone.replace(/\D/g, "")).slice(-12);
  if (email) data.em = email.trim().toLowerCase();
  if (name) {
    const parts = name.trim().split(/\s+/);
    data.fn = parts[0]?.toLowerCase();
    if (parts.length > 1) data.ln = parts[parts.length - 1].toLowerCase();
  }
  if (Object.keys(data).length === 0) return;
  // Re-init with advanced matching params (Pixel ID hardcoded in index.html)
  window.fbq("init", "1645250456577696", data);
  // Also feed Google Ads enhanced conversions
  if (typeof window.gtag === "function") {
    const userData = {};
    if (phone) userData.phone_number = "+91" + phone.replace(/\D/g, "");
    if (email) userData.email = email.trim().toLowerCase();
    window.gtag("set", "user_data", userData);
  }
}

/* -------------- Conversion events -------------- */

/** (a) User clicked any "Spin & Win" CTA. Top-of-funnel intent. */
export function trackSpinStarted(data = {}) {
  _ga("spin_started", { source: data.source || "" });
  _fbq("trackCustom", "SpinStarted", data);
}

/** (b) OTP verified — moment a real lead is captured. */
export function trackOtpVerified(data = {}) {
  if (data.phone || data.name) setAdvancedMatching(data);
  const eventId = _eventId();
  _ga("otp_verified", { source: data.source || "" });
  _fbq("track", "Lead", { source: data.source || "" }, { eventID: eventId });
  _capi("Lead", eventId, { phone: data.phone, name: data.name });
  _gAdsConversion(GADS.LEAD, 500);
}

/** Customer just created a Care+ account (first OTP verify on /me). */
export function trackCompleteRegistration(data = {}) {
  if (data.phone || data.name) setAdvancedMatching(data);
  const eventId = _eventId();
  _ga("sign_up", { method: "phone_otp", source: "care_plus" });
  _fbq("track", "CompleteRegistration", { content_name: "AutoHQ Care+", status: "registered" }, { eventID: eventId });
  _capi("CompleteRegistration", eventId, { phone: data.phone, name: data.name }, { content_name: "AutoHQ Care+" });
}

/** User viewed Care+ package details (lands on /me or scrolls Care+ section). */
export function trackViewContent(data = {}) {
  const eventId = _eventId();
  _ga("view_item", {
    items: [{ item_id: "care_plus_package", item_name: "AutoHQ Care+ Package", price: 1249, quantity: 1 }],
    currency: "INR", value: 1249, ...data,
  });
  _fbq("track", "ViewContent", {
    content_ids: ["care_plus_package"], content_name: "AutoHQ Care+ Package",
    content_type: "product", value: 1249, currency: "INR",
  }, { eventID: eventId });
  _capi("ViewContent", eventId, {}, {
    value: 1249, currency: "INR",
    content_ids: ["care_plus_package"], content_name: "AutoHQ Care+ Package",
  });
}

/** User scrolled to pricing / clicked "See Savings" — strong purchase intent.
 * Fires AddToCart (Meta) + add_to_cart (GA4). Used on the dedicated Care+ LP. */
export function trackAddToCart(data = {}) {
  const eventId = _eventId();
  const value = data.value || 1249;
  const pkgId = data.package_id || "care_plus_package";
  _ga("add_to_cart", {
    items: [{ item_id: pkgId, item_name: "AutoHQ Care+", price: value, quantity: 1 }],
    currency: "INR", value, package_id: pkgId, ...data,
  });
  _fbq("track", "AddToCart", {
    content_ids: [pkgId], content_name: "AutoHQ Care+",
    content_type: "product", value, currency: "INR",
  }, { eventID: eventId });
  _capi("AddToCart", eventId, {}, {
    value, currency: "INR",
    content_ids: [pkgId], content_name: "AutoHQ Care+",
    package_tier: pkgId,
  });
}

/** User clicked "Pay" — Razorpay modal opening. */
export function trackInitiateCheckout(data = {}) {
  const eventId = _eventId();
  const value = data.value || 1249;
  const pkgId = data.package_id || "care_plus_package";
  _ga("begin_checkout", {
    items: [{ item_id: pkgId, item_name: "AutoHQ Care+", price: value, quantity: 1 }],
    currency: "INR", value, package_id: pkgId, ...data,
  });
  _fbq("track", "InitiateCheckout", {
    content_ids: [pkgId], content_name: "AutoHQ Care+",
    num_items: 1, value, currency: "INR",
  }, { eventID: eventId });
  _capi("InitiateCheckout", eventId, {}, {
    value, currency: "INR",
    content_ids: [pkgId], content_name: "AutoHQ Care+",
    package_tier: pkgId,
  });
}

/** Razorpay payment captured + Care+ activated. The big one.
 * Server-side Purchase ALSO fires from /care-plus/verify backend route for max reliability. */
export function trackPurchase(data = {}) {
  const value = data.value || 1249;
  const orderId = data.order_id || "";
  const pkgId = data.package_id || "care_plus_package";
  // Use a deterministic event_id matching backend ('purchase_<package_id>'). Since we don't
  // have package_id here we use order_id which Meta CAPI also dedupes by.
  const eventId = orderId ? `purchase_${orderId}` : _eventId();
  _ga("purchase", {
    transaction_id: orderId,
    value, currency: "INR", package_id: pkgId,
    items: [{ item_id: pkgId, item_name: "AutoHQ Care+", price: value, quantity: 1 }],
  });
  _fbq("track", "Purchase", {
    value, currency: "INR",
    content_ids: [pkgId], content_name: "AutoHQ Care+",
    content_type: "product", num_items: 1,
  }, { eventID: eventId });
  _gAdsConversion(GADS.LEAD, value, "INR", orderId);
  // NOTE: We do NOT call _capi here — backend's /care-plus/verify already fires Purchase
  // CAPI server-side with the same event_id pattern, ensuring 100% delivery + correct dedup.
}

/** (c) Any prize won on Spin or Scratch. */
export function trackPrizeWon(data = {}) {
  _ga("prize_won", {
    prize: data.prize || "",
    source: data.source || "",
    is_grand_prize: !!data.grand_prize,
    car_brand: data.car_brand || "",
  });
  _fbq("trackCustom", "PrizeWon", data);
}

/** (d) ₹5,000 grand prize won — counts as a high-value Lead conversion in Google Ads. */
export function trackGrandPrizeWon(data = {}) {
  _ga("grand_prize_won", {
    prize: data.prize || "",
    source: data.source || "",
    car_brand: data.car_brand || "",
  });
  _fbq("trackCustom", "GrandPrizeWon", data);
  _gAdsConversion(GADS.LEAD, 2000); // higher value for grand prize winners
}

/** (e) Clicked "Claim on WhatsApp" after winning. Direct contact intent. */
export function trackCouponClaimedWhatsapp(data = {}) {
  _ga("coupon_claimed_whatsapp", {
    prize: data.prize || "",
    source: data.source || "",
    coupon_code: data.coupon_code || "",
  });
  _fbq("trackCustom", "CouponClaimedWhatsapp", data);
  _gAdsConversion(GADS.WHATSAPP, 1500);
}

/** (f) Callback / "Request a Callback" form submitted. */
export function trackCallbackRequested(data = {}) {
  if (data.phone || data.name) setAdvancedMatching(data);
  const eventId = _eventId();
  _ga("callback_requested", {
    service_needed: data.service_needed || "",
    car_brand: data.car_brand || "",
  });
  _fbq("track", "Lead", { form: "callback", ...data }, { eventID: eventId });
  _capi("Lead", eventId, { phone: data.phone, name: data.name }, { content_name: "Callback Form" });
  _gAdsConversion(GADS.LEAD, 500);
}

/** (g) Chatbot "Book a Service" flow completed. */
export function trackBookingCompleted(data = {}) {
  _ga("booking_completed", {
    service_needed: data.service_needed || "",
    car_brand: data.car_brand || "",
    preferred_day: data.preferred_day || "",
  });
  _fbq("track", "Schedule", data);
  _gAdsConversion(GADS.BOOKING, 3000);
}

/** (h) Chatbot "Insurance Claim Help" flow completed. */
export function trackInsuranceInquiry(data = {}) {
  _ga("insurance_inquiry", {
    insurer: data.insurer || "",
    car_brand: data.car_brand || "",
  });
  _fbq("track", "Lead", { form: "insurance", ...data });
  _gAdsConversion(GADS.LEAD, 800); // insurance leads are higher value
}

/* -------------- Engagement events -------------- */

/** (i) Chatbot bubble opened. */
export function trackChatOpened(data = {}) {
  _ga("chat_opened", { trigger: data.trigger || "manual" });
  _fbq("trackCustom", "ChatOpened", data);
}

/** (j) User picked a flow inside chatbot (play/book/insurance/why). */
export function trackChatFlowSelected(flow) {
  _ga("chat_flow_selected", { flow });
  _fbq("trackCustom", "ChatFlowSelected", { flow });
}

/** (k) Sticky WhatsApp button clicked. */
export function trackWhatsappFloater() {
  _ga("whatsapp_floater_click");
  _fbq("trackCustom", "WhatsappFloaterClick");
}

/** (l) Specific service card clicked in grid. */
export function trackServiceCardClick(title) {
  _ga("service_card_click", { service: title });
  _fbq("trackCustom", "ServiceCardClick", { service: title });
}

/** (m) Winners Wall scrolled into view (engagement signal). */
export function trackWinnersWallView() {
  _ga("winners_wall_view");
  _fbq("trackCustom", "WinnersWallView");
}

/* -------------- Legacy aliases (kept for backwards compatibility) -------------- */

export function trackLead(data = {}) {
  return trackOtpVerified(data);
}

export function trackCallback(data = {}) {
  return trackCallbackRequested(data);
}

export function trackGA(eventName, params = {}) {
  _ga(eventName, params);
}
